/**
 * Dream Reality — Universal Preview Runtime (v3.0 Modular)
 *
 * Entry point. Activates ONLY when ?preview=true is in the URL.
 * Wires together all core modules and registers message handlers.
 *
 * Zero overhead in production — the entire module no-ops if not in preview mode.
 *
 * This replaces the monolithic preview-runtime.ts with a modular architecture
 * while maintaining 100% compatibility with the admin panel message contract.
 */

import { MessageHandler } from '../core/message-handler.js';
import { InlineEditor } from '../core/inline-editor.js';
import { StyleManager } from '../core/style-manager.js';
import { SectionManager } from '../core/section-manager.js';
import { ArrayUpdater } from '../core/array-updater.js';
import { ConditionEvaluator } from '../core/condition-evaluator.js';
import { FieldUpdater } from '../core/field-updater.js';
import { ComponentRegistry } from '../core/component-registry.js';
import { LifecycleManager } from '../core/lifecycle-manager.js';

// ─── Guard: only run in preview mode ────────────────────────────────

const isPreview = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('preview') === 'true';

if (isPreview) {
  if ((window as any).DreamRealityRuntime) {
    console.warn('[preview-runtime] Already initialized — skipping duplicate load');
  } else {
    initPreviewRuntime();
  }
}

// ─── Module-level state ─────────────────────────────────────────────

let _cachedData: Record<string, Record<string, any>> = {};
let _editabilityMap: Record<string, Record<string, boolean>> = {};
let _isViewOnly = false;

// ─── Main ───────────────────────────────────────────────────────────

function initPreviewRuntime(): void {
  console.log('[preview-runtime] Activated (modular v3.0)');

  // Instantiate core modules
  const messageHandler = new MessageHandler();
  const inlineEditor = new InlineEditor(messageHandler);
  const styleManager = new StyleManager();
  const sectionManager = new SectionManager();
  const arrayUpdater = new ArrayUpdater();
  const conditionEvaluator = new ConditionEvaluator();
  const fieldUpdater = new FieldUpdater();
  const componentRegistry = new ComponentRegistry();
  const lifecycleManager = new LifecycleManager(componentRegistry);

  // ─── Register message handlers ──────────────────────────────────

  messageHandler.registerHandler('full-update', (msg: any) => {
    _editabilityMap = msg.editabilityMap ?? {};
    _isViewOnly = msg.isViewOnly ?? false;
    inlineEditor.setViewOnly(_isViewOnly);
    inlineEditor.setEditabilityMap(_editabilityMap);
    // Set viewport before processing styles so applyResponsiveStyle picks the right breakpoint
    if (msg.viewport) {
      styleManager.setViewport(msg.viewport);
      document.documentElement.setAttribute('data-dr-viewport', msg.viewport);
    }
    handleFullUpdate(msg.data, msg.sections);
  });

  messageHandler.registerHandler('field-update', (msg: any) => {
    handleFieldUpdate(msg.sectionId, msg.field, msg.value);
  });

  messageHandler.registerHandler('section-toggle', (msg: any) => {
    sectionManager.toggleSection(msg.sectionId, msg.enabled);
  });

  messageHandler.registerHandler('style-update', (msg: any) => {
    styleManager.handleStyleUpdate(msg.sectionId, msg.field, msg.styles);
  });

  messageHandler.registerHandler('viewport-change', (msg: any) => {
    console.log('[preview-runtime] viewport-change →', msg.viewport, '(window.innerWidth:', window.innerWidth + ')');
    document.documentElement.setAttribute('data-dr-viewport', msg.viewport);
    styleManager.applyViewport(msg.viewport);
  });

  messageHandler.registerHandler('section-highlight', (msg: any) => {
    sectionManager.highlightSection(msg.sectionId);
  });

  messageHandler.registerHandler('scroll-to-section', (msg: any) => {
    sectionManager.scrollToSection(msg.sectionId);
  });

  // ─── Initialize message listening ───────────────────────────────

  messageHandler.init();

  // ─── Link interception: delegate navigation to admin panel ─────
  // Instead of navigating directly (which desyncs the left panel), we send a
  // navigate-request message so the admin panel can update activePage and reload
  // the iframe to the correct URL with ?preview=true already set.

  document.addEventListener('click', (e) => {
    const a = (e.target as HTMLElement).closest('a');
    if (
      a &&
      a.href &&
      a.host === window.location.host &&
      !a.getAttribute('href')?.startsWith('javascript:')
    ) {
      const hrefAttr = a.getAttribute('href');
      if (hrefAttr && hrefAttr.startsWith('#')) return;

      e.preventDefault();
      const url = new URL(a.href);
      messageHandler.sendToParent({
        type: 'navigate-request',
        pathname: url.pathname,
      });
    }
  });

  // ─── Set up inline editing & image handlers ─────────────────────

  inlineEditor.setupInlineEditing();
  inlineEditor.setupImageClickHandlers();

  // ─── Deselect on click outside editable elements ────────────────

  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    // Do not deselect when clicking anywhere inside a list item card (including padding/icon wrapper)
    if (!target.closest('[data-dr-field]') && !target.closest('[data-dr-list-item]')) {
      messageHandler.sendToParent({ type: 'deselect' });
    }
  });

  // ─── Component registry: scan for data-dr-component elements ────

  registerCanonicalComponents(componentRegistry);
  componentRegistry.scanAndInitialize();

  // ─── Notify parent that the iframe is ready ─────────────────────

  messageHandler.sendToParent({ type: 'ready' });

  // ─── Expose debug API on window ─────────────────────────────────

  (window as any).DreamRealityRuntime = {
    version: '3.0.0',
    messageHandler,
    inlineEditor,
    styleManager,
    sectionManager,
    arrayUpdater,
    conditionEvaluator,
    fieldUpdater,
    componentRegistry,
    lifecycleManager,
    getCachedData: () => _cachedData,
    enableDebug: () => {
      messageHandler.enableDebug();
    },
    getMessageHistory: () => messageHandler.getMessageHistory(),
  };

  // ─── Full Update Handler ──────────────────────────────────────────

  function handleFullUpdate(
    data: Record<string, Record<string, any>>,
    sections: Record<string, { enabled: boolean }>
  ): void {
    // Cache data for condition evaluation
    _cachedData = data;

    // Toggle section visibility
    for (const [sectionId, config] of Object.entries(sections)) {
      sectionManager.toggleSection(sectionId, config.enabled);
    }

    // Update all fields per section
    for (const [sectionId, sectionData] of Object.entries(data)) {
      // Special case: theme section → apply CSS variables to :root
      if (sectionId === 'theme') {
        applyThemeCssVars(sectionData as any);
        continue;
      }

      const sectionEl = document.querySelector(`[data-dr-section="${sectionId}"]`);
      if (!sectionEl) continue;

      for (const [field, value] of Object.entries(sectionData)) {
        // Apply __style keys via style manager
        if (field.endsWith('__style') && typeof value === 'object' && value !== null && !Array.isArray(value)) {
          const baseField = field.slice(0, -7);
          styleManager.handleStyleUpdate(sectionId, baseField, value as Record<string, string>);
          continue;
        }

        if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
          // Nested object — update dot-notation fields
          for (const [nestedKey, nestedValue] of Object.entries(value)) {
            updateFieldElement(sectionEl, `${field}.${nestedKey}`, nestedValue);
          }
        } else if (Array.isArray(value)) {
          arrayUpdater.updateArrayField(sectionEl, field, value, sectionId);
          // Also update dot-notation field references for individual array items
          value.forEach((item: any, index: number) => {
            if (item && typeof item === 'object') {
              for (const [itemKey, itemVal] of Object.entries(item)) {
                updateFieldElement(sectionEl, `${field}.${index}.${itemKey}`, itemVal as any);
              }
            } else {
              updateFieldElement(sectionEl, `${field}.${index}`, item);
            }
          });
        } else {
          updateFieldElement(sectionEl, field, value);
        }
      }
    }

    // Evaluate declarative conditions
    conditionEvaluator.evaluateAll(_cachedData);

    // Re-bind inline editing for any new/replaced DOM nodes
    inlineEditor.setupInlineEditing();
    inlineEditor.setupImageClickHandlers();

    // Restore pointer-events on editable fields after condition evaluation
    inlineEditor.restoreEditability();

    console.log('[preview-runtime] Full update applied');
  }

  // ─── Field Update Handler ───────────────────────────────────────

  function handleFieldUpdate(sectionId: string, field: string, value: any): void {
    // Update cached data FIRST so conditions evaluate correctly
    if (!_cachedData[sectionId]) {
      _cachedData[sectionId] = {};
    }
    _cachedData[sectionId] = { ..._cachedData[sectionId], [field]: value };

    // Find section and update DOM
    const sectionEl = document.querySelector(`[data-dr-section="${sectionId}"]`);
    if (!sectionEl) {
      // Even without a section element, conditions might need re-evaluation
      conditionEvaluator.evaluateAll(_cachedData);
      inlineEditor.restoreEditability();
      return;
    }

    if (Array.isArray(value)) {
      arrayUpdater.updateArrayField(sectionEl, field, value, sectionId);
    } else {
      updateFieldElement(sectionEl, field, value);
    }

    // Re-evaluate all declarative conditions
    conditionEvaluator.evaluateAll(_cachedData);

    // Restore pointer-events on editable fields after condition evaluation
    inlineEditor.restoreEditability();
  }
}

// ─── Element Update Helper ────────────────────────────────────────

function updateFieldElement(container: Element, field: string, value: any): void {
  const el = container.querySelector(`[data-dr-field="${field}"]`) as HTMLElement | null;
  if (!el) return;

  if (value === null || value === undefined) return;

  // Unhide elements that were hidden due to empty initial content
  el.style.removeProperty('display');

  const tagName = el.tagName.toLowerCase();

  if (tagName === 'img') {
    (el as HTMLImageElement).src = String(value);
  } else if (tagName === 'a' && field.includes('href')) {
    (el as HTMLAnchorElement).href = String(value);
  } else if (tagName === 'video') {
    (el as HTMLVideoElement).src = String(value);
  } else if (tagName === 'iframe') {
    (el as HTMLIFrameElement).src = String(value);
  } else if (tagName === 'path') {
    (el as unknown as SVGPathElement).setAttribute('d', String(value));
  } else {
    el.textContent = String(value);
  }
}

// ─── Theme CSS Variables ──────────────────────────────────────────

function applyThemeCssVars(themeData: any): void {
  const style = document.documentElement.style;
  const colors = themeData?.colors ?? {};
  const typography = themeData?.typography ?? {};
  const radius = themeData?.radius ?? {};

  if (colors.primary) style.setProperty('--primary', colors.primary);
  if (colors.primaryForeground) style.setProperty('--primary-foreground', colors.primaryForeground);
  if (colors.background) style.setProperty('--background', colors.background);
  if (colors.surface) style.setProperty('--surface', colors.surface);
  if (colors.muted) style.setProperty('--muted', colors.muted);
  if (colors.border) style.setProperty('--border', colors.border);
  if (typography.fontSans) style.setProperty('--font-sans', typography.fontSans);
  if (typography.fontSerif) style.setProperty('--font-serif', typography.fontSerif);
  if (radius.base) style.setProperty('--radius', radius.base);
}

// ─── Canonical Component Registration ─────────────────────────────

function registerCanonicalComponents(_registry: ComponentRegistry): void {
  // These handlers activate ONLY when templates use data-dr-component attributes.
  // Existing templates continue using dr:content-updated + vanilla JS.
  // Component handler adoption is a separate later step.

  _registry.register('carousel', {
    defaultConfig: { autoRotateInterval: 5000, pauseOnHover: true },
    init(element: HTMLElement, config: Record<string, any>) {
      const slides = element.querySelectorAll('[data-dr-carousel-slide]');
      const dots = element.querySelectorAll('[data-dr-carousel-dot]');
      let current = 0;
      let timer: number | undefined;

      function goTo(index: number): void {
        if (slides[current]) (slides[current] as HTMLElement).style.opacity = '0';
        if (dots[current]) (dots[current] as HTMLElement).style.opacity = '0.4';
        current = index;
        if (slides[current]) (slides[current] as HTMLElement).style.opacity = '1';
        if (dots[current]) (dots[current] as HTMLElement).style.opacity = '1';
      }

      function startAutoRotate(): void {
        if (slides.length <= 1) return;
        timer = window.setInterval(() => {
          goTo((current + 1) % slides.length);
        }, config.autoRotateInterval || 5000);
      }

      dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          if (timer) clearInterval(timer);
          goTo(i);
          startAutoRotate();
        });
      });

      startAutoRotate();

      return {
        id: '',
        type: 'carousel',
        element,
        config,
        update() {
          if (timer) clearInterval(timer);
          current = 0;
          startAutoRotate();
        },
        destroy() {
          if (timer) clearInterval(timer);
        },
      };
    },
  });

  _registry.register('accordion', {
    defaultConfig: { allowMultiple: false },
    init(element: HTMLElement, config: Record<string, any>) {
      function initItems(): void {
        const items = element.querySelectorAll('[data-dr-accordion-item]');
        items.forEach((item) => {
          const trigger = item.querySelector('[data-dr-accordion-trigger]');
          const panel = item.querySelector('[data-dr-accordion-panel]') as HTMLElement | null;
          if (!trigger || !panel) return;

          if (trigger.getAttribute('aria-expanded') === 'true') {
            panel.style.maxHeight = panel.scrollHeight + 'px';
          }

          trigger.addEventListener('click', () => {
            const isOpen = trigger.getAttribute('aria-expanded') === 'true';

            if (!config.allowMultiple) {
              items.forEach((other) => {
                const t = other.querySelector('[data-dr-accordion-trigger]');
                const p = other.querySelector('[data-dr-accordion-panel]') as HTMLElement | null;
                if (t) t.setAttribute('aria-expanded', 'false');
                if (p) p.style.maxHeight = '0';
              });
            }

            if (!isOpen) {
              trigger.setAttribute('aria-expanded', 'true');
              panel.style.maxHeight = panel.scrollHeight + 'px';
            }
          });
        });
      }

      initItems();

      return {
        id: '',
        type: 'accordion',
        element,
        config,
        update() { initItems(); },
        destroy() {},
      };
    },
  });

  _registry.register('tabs', {
    defaultConfig: {},
    init(element: HTMLElement, config: Record<string, any>) {
      function initTabs(): void {
        const tabs = element.querySelectorAll('[data-dr-tab]');
        const panels = element.querySelectorAll('[data-dr-tab-panel]');

        tabs.forEach((tab) => {
          tab.addEventListener('click', () => {
            const id = tab.getAttribute('data-dr-tab');

            tabs.forEach((t) => {
              t.classList.remove('border-b-2', 'border-primary');
              t.classList.add('text-muted');
            });
            panels.forEach((p) => { (p as HTMLElement).style.display = 'none'; });

            tab.classList.remove('text-muted');
            tab.classList.add('border-b-2', 'border-primary');
            const panel = element.querySelector(`[data-dr-tab-panel="${id}"]`) as HTMLElement | null;
            if (panel) panel.style.display = '';
          });
        });
      }

      initTabs();

      return {
        id: '',
        type: 'tabs',
        element,
        config,
        update() { initTabs(); },
        destroy() {},
      };
    },
  });

  _registry.register('modal', {
    defaultConfig: {},
    init(element: HTMLElement, config: Record<string, any>) {
      const modalId = element.getAttribute('data-dr-modal') || '';

      function openModal(): void {
        element.style.display = 'flex';
        document.body.style.overflow = 'hidden';
      }

      function closeModal(): void {
        element.style.display = 'none';
        document.body.style.overflow = '';
      }

      // Triggers
      document.querySelectorAll(`[data-dr-modal-trigger="${modalId}"]`).forEach((trigger) => {
        trigger.addEventListener('click', () => openModal());
      });

      // Close button
      const closeBtn = element.querySelector('[data-dr-modal-close]');
      if (closeBtn) closeBtn.addEventListener('click', closeModal);

      // Backdrop click
      element.addEventListener('click', (e) => { if (e.target === element) closeModal(); });

      // ESC key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && element.style.display === 'flex') closeModal();
      });

      return {
        id: '',
        type: 'modal',
        element,
        config,
        update() {},
        destroy() {},
      };
    },
  });

  _registry.register('video-player', {
    defaultConfig: {},
    init(element: HTMLElement, config: Record<string, any>) {
      const trigger = element.querySelector('[data-dr-play-trigger]');
      const player = element.querySelector('[data-dr-video-player]') as HTMLVideoElement | null;
      const poster = element.querySelector('img[data-dr-field]') as HTMLImageElement | null;
      const playBtnParent = trigger?.parentElement;

      if (trigger && player) {
        trigger.addEventListener('click', () => {
          if (poster) poster.style.display = 'none';
          if (playBtnParent) playBtnParent.style.display = 'none';
          player.classList.remove('hidden');
          player.play();
        });
      }

      return {
        id: '',
        type: 'video-player',
        element,
        config,
        update() {},
        destroy() {},
      };
    },
  });
}
